import React from 'react';
import { SlRow, useStacklineTheme, hexToRgba } from '@stackline/ui';
import Box from '@mui/material/Box';
import { SplineChartV2, SplineChartV2Series } from 'src/components/BeaconV2Components/SplineChartV2';
import { Text } from '../Generic/Text';
import { SlColumn } from 'src/components/BeaconV2Components/Generic/SlColumn';
import {
  Direction,
  getColorForMetricChangeValue,
  getIconForMetricChangeForDirection
} from 'src/components/BeaconV2Components/utils/chartStyles';
import {
  BEACON_PRO_LARGE_CARD_HEIGHT,
  BEACON_PRO_CARD_WIDTH,
  FORECAST_KEY_METRICS_CARD_WIDTH
} from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { ValueOf } from 'sl-api-connector';
import { METRICTYPE } from 'src/utils/entityDefinitions';

export interface MetricSummaryTrendCardV2Props {
  /**
   * Metric title, e.g. "Total Traffic"
   */
  title: string;
  /**
   * Formatted value of the metric, e.g. "13.58M" or "$11.48"
   */
  value: string;
  /**
   * Formatted percentage change. Should be an absolute value.
   * The sign is given through `direction`
   */
  change: string;
  /**
   * Primary series data for the spline chart
   */
  primarySeries: SplineChartV2Series;
  /**
   * Comparison series data for the spline chart
   */
  comparisonSeries?: SplineChartV2Series;
  /**
   * Forecast series data for the spline chart
   */
  forecastSeries?: SplineChartV2Series;
  /**
   * Metric type for formatting
   */
  metricType: ValueOf<typeof METRICTYPE>;
  /**
   * When selected is true, shows a gray background
   */
  selected: boolean;
  /**
   * If the change is positive or negative
   */
  direction: Direction;
  onClick: () => void;
  children?: React.ReactNode;
  /**
   * Override for prior period detection.
   * When true, merges comparison and primary/forecast into one continuous line.
   */
  isPriorPeriodOverride?: boolean;
}

/**
 * The metric summary card with a SplineChartV2 preview for use on the Beacon summary page.
 */
const MetricSummaryTrendCardV2 = ({
  variant,
  children,
  ...rest
}: MetricSummaryTrendCardV2Props & { variant: 'large' | 'small' | 'medium' }) => {
  if (variant === 'small') {
    return <MetricsSummaryTrendCardV2Small {...rest} />;
  }
  return (
    <MetricSummaryTrendCardV2Large variant={variant} {...rest}>
      {children}
    </MetricSummaryTrendCardV2Large>
  );
};

/**
 * Large variant of the metric summary card
 */
export const MetricSummaryTrendCardV2Large = ({
  title,
  value,
  change,
  primarySeries,
  comparisonSeries,
  forecastSeries,
  metricType,
  selected,
  direction,
  onClick,
  children,
  variant,
  isPriorPeriodOverride
}: MetricSummaryTrendCardV2Props & { variant: 'large' | 'medium' }) => {
  const theme = useStacklineTheme();
  const backgroundColor = hexToRgba(theme.colors.secondaryPorcelain, 0.5);

  return (
    <Box
      sx={{
        height: children ? undefined : BEACON_PRO_LARGE_CARD_HEIGHT,
        minHeight: BEACON_PRO_LARGE_CARD_HEIGHT,
        width: variant === 'large' ? BEACON_PRO_CARD_WIDTH : FORECAST_KEY_METRICS_CARD_WIDTH,
        backgroundColor: selected ? hexToRgba(theme.colors.secondaryPorcelain, 0.5) : undefined,
        cursor: 'pointer',
        '&:hover': {
          backgroundColor
        },
        borderRadius: theme.spacing.sm
      }}
      onClick={onClick}
      role="button"
    >
      <SlColumn spacing="sm" horizontalInset="md" verticalInset="md">
        <Text variant="subtitle3" color="secondary" transform="uppercase">
          {title}
        </Text>
        <SlRow spacing="sm" verticalPosition="center">
          <Text variant="h2">{value}</Text>
          <SlRow verticalPosition="center" spacing="xs">
            {getIconForMetricChangeForDirection(direction, title)}
            <Text variant="subtitle3" color={getColorForMetricChangeValue(null, direction, title)}>
              {change}
            </Text>
          </SlRow>
        </SlRow>
        <SlColumn spacing="lg">
          <SplineChartV2
            primarySeries={primarySeries}
            comparisonSeries={comparisonSeries}
            forecastSeries={forecastSeries}
            metricType={metricType}
            preview="large"
            width={variant === 'medium' ? 210 : undefined}
            isPriorPeriodOverride={isPriorPeriodOverride}
          />
          {children}
        </SlColumn>
      </SlColumn>
    </Box>
  );
};

/**
 * Small variant of the metric summary card
 */
export const MetricsSummaryTrendCardV2Small = ({
  title,
  value,
  change,
  primarySeries,
  comparisonSeries,
  forecastSeries,
  metricType,
  selected,
  direction,
  onClick,
  isPriorPeriodOverride
}: MetricSummaryTrendCardV2Props) => {
  const theme = useStacklineTheme();
  const backgroundColor = hexToRgba(theme.colors.secondaryPorcelain, 0.5);

  return (
    <Box
      sx={{
        height: '96px',
        width: BEACON_PRO_CARD_WIDTH,
        backgroundColor: selected ? backgroundColor : undefined,
        cursor: 'pointer',
        '&:hover': {
          backgroundColor
        },
        borderRadius: theme.spacing.sm
      }}
      onClick={onClick}
    >
      <SlColumn horizontalInset="md" style={{ paddingTop: '13px', paddingBottom: '13px' }} widths="full">
        <SlRow>
          <SlColumn>
            <Text variant="subtitle3" color="secondary" transform="uppercase">
              {title}
            </Text>
          </SlColumn>
        </SlRow>
        <SlRow verticalInset="sm" horizontalPosition="space-between">
          <SlColumn spacing="sm">
            <Text variant="body0">{value}</Text>
            <SlRow spacing="xs" verticalPosition="center">
              {getIconForMetricChangeForDirection(direction, title)}
              <Text variant="subtitle3" color={getColorForMetricChangeValue(null, direction, title)}>
                {change}
              </Text>
            </SlRow>
          </SlColumn>
          <Box sx={{ transform: 'translateY(-32px)' }}>
            <SplineChartV2
              primarySeries={primarySeries}
              comparisonSeries={comparisonSeries}
              forecastSeries={forecastSeries}
              metricType={metricType}
              preview="small"
              isPriorPeriodOverride={isPriorPeriodOverride}
            />
          </Box>
        </SlRow>
      </SlColumn>
    </Box>
  );
};

export default MetricSummaryTrendCardV2;
